import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { MINE_BOARD_SIZE } from '@/config/original'
import type { MineAutoBetSettings, MineBetMode, MineGameResult, MineGameState, MineRoundState, MineTile } from '@/types/mine'
import type { RootState } from '@/store/store'

export const initialMineAutoBetSettings: MineAutoBetSettings = {
	betCount: 0,
	isInfinite: true,
	stopOnProfit: 0,
	stopOnLoss: 0,
}

export const initialMineState: MineGameState = {
	phase: 'ready',
	mineGameId: null,
	currencyCode: 'USD',
	betMode: 'manual',
	betAmount: '1',
	mineCount: 3,
	revealedTiles: [],
	autoSelectedPoints: [],
	autoBetSettings: initialMineAutoBetSettings,
	autoBetRunning: false,
	isSubmitting: false,
	isResultVisible: false,
	currentMultiplier: 0,
	currentPayout: 0,
	currentResult: { multiplier: 0, amount: 0, payout: 0, profit: 0 },
	winAmount: 0,
}

const mineSlice = createSlice({
	name: 'mine',
	initialState: initialMineState,
	reducers: {
		resetMineGame(state) {
			return {
				...initialMineState,
				currencyCode: state.currencyCode,
				betAmount: state.betAmount,
				mineCount: state.mineCount,
				betMode: state.betMode,
				autoBetSettings: state.autoBetSettings,
			}
		},
		setMineCurrencyCode(state, action: PayloadAction<string>) {
			state.currencyCode = action.payload
		},
		setMineBetMode(state, action: PayloadAction<MineBetMode>) {
			state.betMode = action.payload
		},
		setMineBetAmount(state, action: PayloadAction<string>) {
			state.betAmount = action.payload
		},
		setMineCount(state, action: PayloadAction<number>) {
			state.mineCount = action.payload
			const maxSafeTiles = MINE_BOARD_SIZE - state.mineCount
			if (state.autoSelectedPoints.length > maxSafeTiles) {
				state.autoSelectedPoints = state.autoSelectedPoints.slice(0, maxSafeTiles)
			}
		},
		setMineAutoBetSettings(state, action: PayloadAction<Partial<MineAutoBetSettings>>) {
			state.autoBetSettings = { ...state.autoBetSettings, ...action.payload }
		},
		setMineAutoBetRunning(state, action: PayloadAction<boolean>) {
			state.autoBetRunning = action.payload
		},
		setMineSubmitting(state, action: PayloadAction<boolean>) {
			state.isSubmitting = action.payload
		},
		setMineRevealedTiles(state, action: PayloadAction<MineTile[]>) {
			state.revealedTiles = action.payload
		},
		addMinePendingTile(state, action: PayloadAction<number>) {
			if (state.revealedTiles.some((tile) => tile.point === action.payload)) {
				return
			}

			state.revealedTiles.push({
				point: action.payload,
				content: 'hidden',
				isRevealed: false,
			})
		},
		toggleMineAutoTile(state, action: PayloadAction<number>) {
			const point = action.payload
			const index = state.autoSelectedPoints.indexOf(point)

			if (index >= 0) {
				state.autoSelectedPoints.splice(index, 1)
				return
			}

			const maxSafeTiles = MINE_BOARD_SIZE - state.mineCount
			if (state.autoSelectedPoints.length >= maxSafeTiles) {
				return
			}

			state.autoSelectedPoints.push(point)
		},
		clearMineAutoTiles(state) {
			state.autoSelectedPoints = []
		},
		applyMineRoundState(state, action: PayloadAction<MineRoundState>) {
			const data = action.payload
			state.revealedTiles = data.tiles
			state.currentMultiplier = data.multiplier
			state.currentPayout = data.payout
			state.mineGameId = data.mineGame?.id ?? null
			state.isSubmitting = false

			if (data.mineGame?.mine_count != null) {
				state.mineCount = data.mineGame.mine_count
			}

			if (data.mineGame?.amount != null) {
				state.betAmount = String(data.mineGame.amount)
			}

			if (data.status === 'bet') {
				state.phase = 'live'
				state.isResultVisible = false
				return
			}

			state.phase = 'ready'
			state.mineGameId = null
			state.isResultVisible = data.status === 'win'

			if (data.result) {
				state.currentResult = data.result
				state.winAmount = data.result.profit
			}
		},
		dismissMineResult(state) {
			state.isResultVisible = false
			state.revealedTiles = []
			if (!state.autoBetRunning) {
				state.autoSelectedPoints = []
			}
			state.currentMultiplier = 0
			state.currentPayout = 0
		},
	},
})

export const {
	resetMineGame,
	setMineCurrencyCode,
	setMineBetMode,
	setMineBetAmount,
	setMineCount,
	setMineAutoBetSettings,
	setMineAutoBetRunning,
	setMineSubmitting,
	setMineRevealedTiles,
	addMinePendingTile,
	toggleMineAutoTile,
	clearMineAutoTiles,
	applyMineRoundState,
	dismissMineResult,
} = mineSlice.actions

export const selectMineGame = (state: RootState) => state.mine

export default mineSlice.reducer
